import { clamp, HintAnimation } from '../hint-motion';
import {
  abs,
  C,
  click,
  cursorTemplate,
  DIVIDER_H,
  glass,
  icon,
  mountTemplate,
  MOTION,
  nextUid,
  orbiTemplate,
  PRESSED,
  Refs,
  renderCamera,
  renderCursor,
  ORBI_FACE_SECONDS,
  renderOrbi,
  stage,
} from './scene-kit';

/**
 * Portal-Hinweis 6 „Platziere KI-Agenten in deinem LMS" — Port des
 * Claude-Design-Films `Platzierungen Loop.dc.html` (Projekt „Portal-Hinweis 6",
 * Datei `hinweis-6-platzierungen-loop.jsx`). KI-Agenten › Platzierungen →
 * Agent per Linien auf 5 Kurse → „Platzieren" → Kurs-Sync-Schalter an.
 * Bühne 640×354, 8,0 s Loop; T = Abspielzeit / 1,6.
 */

const STRETCH = 1.6;
const AUTHORED = 5.0;

// Szenenanfänge in Autorenzeit (Summe der `nat`-Längen aus OM_SCENES).
const CUES = { Seite: 0.7, Mapping: 1.2, Platzieren: 2.4, Sync: 3.2, Loop: 4.2 };

const OFF = '#B7C1CF'; // Schalter aus (wie nb-toggle im Portal)

// Geometrie: Navigation links, Seite rechts (Titel frei auf dem Verlauf, darunter eine Glasfläche)
const NX = 14, NY = 18, NW = 140, NH = 318;
const NAV = [
  { ic: 'orbi', label: 'KI-Agenten', y: 12 },
  { label: 'Agenten', y: 48, sub: true },
  { label: 'Platzierungen', y: 82, sub: true },
  { ic: 'brain', label: 'Wissensbasis', y: 120 },
  { ic: 'plug', label: 'Integrationen', y: 156 },
];
const SEL = 2;
const RX = 166, RY = 62, RW = 460, RH = 274;
const TABS: [string, number, number][] = [['Moodle', 14, 76], ['ILIAS', 92, 60]];
const AGENT = { x: 14, y: 103, w: 100, h: 88 };
const COURSES = ['Statistik I', 'Statistik II', 'Methoden der Empirie', 'Datenanalyse', 'Forschungsdesign'];
const ROW_Y = (i: number) => 72 + i * 30;
const ROW_H = 30;
const COL = { chk: 160, mark: 182, name: 205, tutor: 330, sync: 420, dot: 450, end: 452 };
const BAR = { y: 230, h: 30, pillX: 98, pillW: 182, btnX: 290, btnW: 96 };

const ORBI_DOT =
  'width:8px;height:8px;border-radius:999px;flex:0 0 auto;background:radial-gradient(circle at 32% 26%, #fff 0, rgb(255 255 255 / .75) 22%, rgb(180 127 255 / .45) 62%, rgb(59 130 246 / .7) 100%);box-shadow:0 1px 2px rgb(38 65 112 / .3);';

function template(uid: string): string {
  const nav = NAV.map(
    (n, i) =>
      `<div style="${abs(8, n.y, NW - 16, n.sub ? 32 : 34, 'display:flex;')}">` +
      (i === SEL ? `<div data-r="navSel" style="position:absolute;inset:0;border-radius:12px;opacity:0;${PRESSED}"></div>` : '') +
      `<div ${i === SEL ? 'data-r="navLabel"' : ''} style="position:relative;flex:1;display:flex;align-items:center;gap:9px;padding:${n.sub ? '0 10px 0 35px' : '0 10px'};font-size:12.5px;font-weight:500;color:${C.text};white-space:nowrap;">` +
      (n.ic === 'orbi' ? orbiTemplate('navOrbi', 16) : n.ic ? icon(n.ic, 16) : '') +
      `${n.label}</div></div>`,
  ).join('');

  const tabs =
    `<div data-r="tabs" style="opacity:0;">` +
    TABS.map(
      ([label, x, w], i) =>
        `<div style="${abs(x, 12, w, 28, `display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:${i === 0 ? 600 : 500};color:${i === 0 ? C.text : C.muted};white-space:nowrap;`)}">${label}</div>`,
    ).join('') +
    `<div style="${abs(14, 42, RW - 28, 2, `background:${DIVIDER_H};`)}"></div>` +
    `<div data-r="tabUnder" style="${abs(TABS[0][1] + 10, 40, TABS[0][2] - 20, 3, `border-radius:2px;background:${C.action};opacity:0;`)}"></div>` +
    `</div>`;

  const hd = (left: number, text: string) =>
    `<div data-r="colHead" style="position:absolute;left:${left}px;top:54px;height:12px;font-size:10px;font-weight:500;color:${C.muted};white-space:nowrap;opacity:0;">${text}</div>`;

  const rows = COURSES.map((name, i) => {
    const y = ROW_Y(i);
    const cell = 'display:flex;align-items:center;white-space:nowrap;';
    return (
      `<div data-r="row" style="opacity:0;">` +
      (i > 0 ? `<div style="${abs(COL.chk, y - 1, COL.end - COL.chk, 2, `background:${DIVIDER_H};`)}"></div>` : '') +
      `<div style="${abs(COL.chk, y + 7, 16, 16)}"><div style="position:relative;width:16px;height:16px;">` +
      `<div style="position:absolute;inset:0;border-radius:5px;background:rgb(255 255 255 / .55);box-shadow:inset 0 2px 3px rgb(47 76 121 / .22), inset 0 -1px 1px rgb(255 255 255 / .9), 0 0 0 1px rgb(83 109 145 / .28);"></div>` +
      `<div data-r="check" style="position:absolute;inset:0;border-radius:5px;background:${C.action};opacity:0;box-shadow:0 3px 6px -3px rgb(37 99 235 / .6);display:flex;align-items:center;justify-content:center;">${icon('check', 12, '#fff', 2.6)}</div>` +
      `</div></div>` +
      `<div style="${abs(COL.mark, y + 6, 18, 18, 'border-radius:999px;background:#F98012;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;letter-spacing:-0.02em;color:#fff;line-height:1;padding-bottom:1.5px;box-sizing:border-box;box-shadow:inset 0 1px 1px rgb(255 255 255 / .35), 0 3px 6px -3px rgb(38 65 112 / .45);')}">m</div>` +
      `<div style="${abs(COL.name, y, 118, ROW_H, `${cell}font-size:10.5px;font-weight:600;color:${C.text};`)}">${name}</div>` +
      `<div data-r="tutor" style="${abs(COL.tutor, y, 84, ROW_H, `${cell}gap:4px;opacity:0;font-size:10px;font-weight:500;color:${C.text};`)}"><span style="${ORBI_DOT}"></span>Statistik-Tutor</div>` +
      `<div style="${abs(COL.sync, y + 7.5, 26, 15)}"><div style="position:relative;width:26px;height:15px;">` +
      `<div style="position:absolute;inset:0;border-radius:999px;background:${OFF};box-shadow:inset 0 1px 2px rgb(47 76 121 / .25);"></div>` +
      `<div data-r="toggleOn" style="position:absolute;inset:0;border-radius:999px;background:${C.action};opacity:0;box-shadow:inset 0 1px 2px rgb(20 50 120 / .25);"></div>` +
      `<div data-r="knob" style="${abs(2, 2, 11, 11, 'border-radius:999px;background:#fff;box-shadow:0 1px 2px rgb(38 65 112 / .35);')}"></div>` +
      `</div></div>` +
      `<div data-r="syncDot" style="${abs(COL.dot, y + 12, 6, 6, `border-radius:999px;background:${C.ok};opacity:0;`)}"></div>` +
      `</div>`
    );
  }).join('');

  const links = COURSES.map((_, i) => {
    const x0 = AGENT.x + AGENT.w + 2, y0 = AGENT.y + AGENT.h / 2;
    const x1 = COL.chk - 4, y1 = ROW_Y(i) + ROW_H / 2;
    const d = `M${x0} ${y0} C${x0 + 26} ${y0} ${x1 - 26} ${y1} ${x1} ${y1}`;
    return (
      `<svg data-r="link" width="${RW}" height="${RH}" style="position:absolute;left:0;top:0;overflow:visible;pointer-events:none;opacity:0;">` +
      `<path data-r="linkGlow" d="${d}" fill="none" stroke="${C.accent}" stroke-width="6" stroke-linecap="round" opacity="0"/>` +
      `<path data-r="linkLine" d="${d}" pathLength="1" fill="none" stroke="${C.accent}" stroke-width="2" stroke-linecap="round" stroke-dasharray="1 1" stroke-dashoffset="1"/>` +
      `</svg>`
    );
  }).join('');

  const agent =
    `<div data-r="agent" style="${abs(AGENT.x, AGENT.y, AGENT.w, AGENT.h, 'opacity:0;')}">` +
    `<div style="position:absolute;inset:0;border-radius:18px;background:linear-gradient(150deg, rgb(255 255 255 / .34), rgb(255 255 255 / .12));box-shadow:inset 0 1px 1px rgb(255 255 255 / .85), inset 0 -1px 1px rgb(52 76 117 / .10);"></div>` +
    `<div style="${abs(11, 11, 30, 30)}">${orbiTemplate('agentOrbi', 30)}</div>` +
    `<div style="${abs(11, 50, AGENT.w - 14, 16, `font-size:11px;font-weight:600;color:${C.text};white-space:nowrap;`)}">Statistik-Tutor</div>` +
    `<div style="${abs(11, 66, AGENT.w - 14, 14, `font-size:10px;font-weight:400;color:${C.muted};white-space:nowrap;`)}">Alpha Tutor</div>` +
    `</div>`;

  const pill = 'position:absolute;border-radius:999px;display:flex;align-items:center;justify-content:center;gap:6px;white-space:nowrap;font-size:11px;font-weight:600;';
  const bar =
    `<div data-r="bar" style="opacity:0;">` +
    `<div style="${abs(14, BAR.y - 10, COL.end - 14, 2, `background:${DIVIDER_H};`)}"></div>` +
    `<div style="${abs(18, BAR.y, 80, BAR.h, `display:flex;align-items:center;gap:5px;white-space:nowrap;font-size:11px;font-weight:500;color:${C.muted};`)}">` +
    `<span style="font-size:11px;font-weight:600;color:${C.text};font-variant-numeric:tabular-nums;">5</span>ausgewählt</div>` +
    `<div style="${abs(BAR.pillX, BAR.y, BAR.pillW, BAR.h)}${pill}font-weight:500;color:${C.muted};background:rgb(255 255 255 / .62);box-shadow:inset 0 1px 1px rgb(255 255 255 / .9), 0 0 0 1px rgb(83 109 145 / .18), 0 3px 8px -5px rgb(38 65 112 / .3);">` +
    `Agent wählen:<span style="font-weight:600;color:${C.text};">Statistik-Tutor</span>` +
    `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="${C.icon}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="display:block;flex:0 0 auto;"><path d="m7 10 5 5 5-5"/></svg></div>` +
    `<div data-r="placeBtn" style="${abs(BAR.btnX, BAR.y, BAR.btnW, BAR.h)}${pill}color:#fff;background:${C.action};box-shadow:0 6px 14px -8px rgb(37 99 235 / .8), inset 0 1px 0 rgb(255 255 255 / .25);">Platzieren</div>` +
    `</div>`;

  const title = `<div data-r="title" style="${abs(RX + 12, 24, 260, 28, `opacity:0;font-size:20px;font-weight:600;letter-spacing:-0.02em;color:${C.text};white-space:nowrap;`)}">Platzierungen</div>`;

  const page = glass(
    30,
    0.42,
    `left:${RX}px;top:${RY}px;width:${RW}px;height:${RH}px;opacity:0;`,
    tabs + hd(COL.name, 'Kurs') + hd(COL.tutor, 'Tutor') + hd(COL.sync - 16, 'Kurs-Sync') + rows + links + agent + bar,
    'data-r="page"',
  );

  return stage(glass(26, 0.42, `left:${NX}px;top:${NY}px;width:${NW}px;height:${NH}px;`, nav) + title + page + cursorTemplate(uid));
}

export class PlaceAgentsAnimation implements HintAnimation {
  readonly width = 640;
  readonly height = 354;
  readonly playbackSeconds = AUTHORED * STRETCH;
  /** Schlussbild: platziert, alle Kurs-Sync-Schalter an (T = 4,0). */
  readonly stillAt = 4.0 * STRETCH;

  private refs: Refs = {};

  mount(host: HTMLElement): void {
    this.refs = mountTemplate(host, template(nextUid('al-hint-place')));
  }

  render(t: number): void {
    this.frame(clamp(t / STRETCH, 0, AUTHORED));
  }

  private one(key: string): HTMLElement {
    return this.refs[key][0];
  }

  private fadeIn(el: HTMLElement, o: number, dy: number): void {
    el.style.opacity = String(o);
    el.style.transform = `translateY(${(1 - o) * dy}px)`;
  }

  /** Ein Bild zur Autorenzeit T — Werte wie `Piece` in hinweis-6-platzierungen-loop.jsx. */
  private frame(T: number): void {
    const { Seite: se, Mapping: mp, Platzieren: pz, Sync: sy, Loop: lp } = CUES;
    const live = 1 - MOTION.glide(lp, lp + 0.6, T);

    const c1 = se - 0.12, c2 = pz + 0.36, c3 = sy + 0.36;
    const ks = [c1, c2, c3].map((at) => click(at, T));
    const press = Math.max(...ks.map((k) => k.press));
    const ring = ks.map((k) => k.ring).find((r) => r > 0 && r < 1) || 0;

    // Zeiger (Bühnenkoordinaten)
    const REST = [598, 318];
    const P = [NX + 78, NY + NAV[SEL].y + 16];
    const B = [RX + BAR.btnX + BAR.btnW / 2, RY + BAR.y + BAR.h / 2];
    const S0 = [RX + COL.sync + 13, RY + ROW_Y(0) + ROW_H / 2];
    const ct = [0, 0.1, c1 - 0.06, c1 + 0.08, se + 0.4, pz + 0.1, c2 - 0.06, c2 + 0.1, sy + 0.05, c3 - 0.06, c3 + 0.1, lp + 0.05, lp + 0.6, 99];
    const pts = [REST, REST, P, P, REST, REST, B, B, B, S0, S0, S0, REST, REST];
    const cx = MOTION.path(ct, pts.map((p) => p[0]), T);
    const cy = MOTION.path(ct, pts.map((p) => p[1]), T);
    renderCursor(this.one('ring'), this.one('arrow'), cx, cy, press, ring);

    // Navigation + Seite
    const sel = MOTION.enter(c1, c1 + 0.12, T) * live;
    this.one('navSel').style.opacity = String(sel);
    this.one('navLabel').style.fontWeight = sel > 0.5 ? '600' : '500';
    this.fadeIn(this.one('title'), MOTION.enter(se, se + 0.24, T) * live, 6);
    this.fadeIn(this.one('page'), MOTION.enter(se + 0.05, se + 0.35, T) * live, 8);
    this.fadeIn(this.one('tabs'), MOTION.enter(se + 0.15, se + 0.4, T) * live, 5);
    this.one('tabUnder').style.opacity = String(MOTION.enter(se + 0.25, se + 0.45, T) * live);

    // Mapping: Agent-Kachel, Kurszeilen, Linien, Checkboxen
    const agentO = MOTION.enter(mp, mp + 0.26, T) * live;
    const agent = this.one('agent');
    agent.style.opacity = String(agentO);
    agent.style.transform = `translateY(${(1 - agentO) * 10}px) scale(${0.96 + 0.04 * agentO})`;
    const headO = String(MOTION.enter(mp + 0.02, mp + 0.26, T) * live);
    this.refs['colHead'].forEach((el) => (el.style.opacity = headO));

    const c2Done = MOTION.enter(c2, c2 + 0.08, T);
    const pulse = Math.sin(Math.PI * clamp((T - (c2 + 0.06)) / 0.3, 0, 1));
    const lineO = (1 - MOTION.enter(c2 + 0.32, c2 + 0.58, T)) * live;
    const syncAt = COURSES.map((_, i) => (i === 0 ? c3 : c3 + 0.12 + 0.09 * (i - 1)));

    COURSES.forEach((_, i) => {
      const rowO = MOTION.enter(mp + 0.04 + 0.05 * i, mp + 0.3 + 0.05 * i, T) * live;
      const row = this.refs['row'][i];
      row.style.opacity = String(rowO);
      row.style.transform = `translateX(${(1 - rowO) * 8}px)`;

      const s = mp + 0.24 + 0.17 * i;
      const draw = MOTION.enter(s, s + 0.3, T);
      const on = MOTION.enter(s + 0.26, s + 0.38, T) * live;
      const check = this.refs['check'][i];
      check.style.opacity = String(on);
      check.style.transform = `scale(${0.7 + 0.3 * on})`;
      this.refs['link'][i].style.opacity = draw > 0.001 ? String(lineO) : '0';
      this.refs['linkLine'][i].setAttribute('stroke-dashoffset', String(1 - draw));
      this.refs['linkLine'][i].setAttribute('stroke-width', String(2 + 1.2 * pulse));
      this.refs['linkGlow'][i].setAttribute('opacity', String(0.22 * pulse));

      const tutor = MOTION.enter(c2 + 0.08 + 0.06 * i, c2 + 0.3 + 0.06 * i, T) * live;
      const tutorEl = this.refs['tutor'][i];
      tutorEl.style.opacity = String(tutor);
      tutorEl.style.transform = `translateX(${(1 - tutor) * 6}px)`;

      const sync = MOTION.enter(syncAt[i], syncAt[i] + 0.12, T) * live;
      this.refs['toggleOn'][i].style.opacity = String(sync);
      this.refs['knob'][i].style.left = `${2 + 11 * sync}px`;
      const dot = Math.sin(Math.PI * clamp((T - (syncAt[i] + 0.04)) / 0.4, 0, 1)) * live;
      const dotEl = this.refs['syncDot'][i];
      dotEl.style.opacity = String(dot);
      dotEl.style.transform = `scale(${0.5 + 0.5 * dot})`;
    });

    // Aktionsleiste „Platzieren"
    this.fadeIn(this.one('bar'), MOTION.enter(pz, pz + 0.22, T) * live, 8);
    const btn = this.one('placeBtn');
    btn.style.background = c2Done > 0.5 ? C.hover : C.action;
    btn.style.transform = `scale(${1 - 0.04 * ks[1].press})`;

    // Orbi auf der Agent-Kachel lacht nach dem Platzieren
    renderOrbi(
      this.refs,
      'agentOrbi',
      MOTION.glide(c2 + 0.12, c2 + 0.12 + ORBI_FACE_SECONDS, T) * live,
      Math.sin(Math.PI * clamp((T - (c2 + 0.08)) / 0.35, 0, 1)),
    );

    // Kamera
    const ck = [0, c1, se + 0.3, mp + 0.2, pz + 0.2, sy, sy + 0.32, lp, lp + 0.6, 99];
    const s = MOTION.path(ck, [1, 1.04, 1, 1.05, 1.05, 1.05, 1.45, 1.45, 1, 1], T);
    const fx = MOTION.path(ck, [320, 290, 320, 360, 380, 380, 420, 420, 320, 320], T);
    const fy = MOTION.path(ck, [176, 170, 176, 190, 196, 196, 210, 210, 176, 176], T);
    renderCamera(this.one('camera'), fx, fy, s);
  }
}
